"use client";

import { forwardRef, type InputHTMLAttributes, type ReactNode } from "react";
import styles from "./Form.module.css";

/**
 * Checkbox — `<input type="checkbox">` zawsze opakowany w `<label>` żeby
 * cały tekst był klikalny.
 *
 * **Dwa sposoby użycia:**
 *   1. `<Checkbox label="..." />` — label jako prop
 *   2. `<Checkbox>...</Checkbox>` — label jako children (np. z linkami,
 *      formatowaniem, dangerouslySetInnerHTML)
 *
 * **Walidacja:** prop `error?: string` renderuje czerwony komunikat z ikoną
 * (`styles.errorText`) pod checkboxem — wizualnie spójny z `<Field>` /
 * `<Input>` (ten sam komponent CSS). Aktywuje też `aria-invalid` dla a11y.
 *
 * **A11y:** label-wrap = screen reader przeczyta tekst po stanie checkboxa.
 * Focus-visible ma outline (`--color-primary`) — `accent-color` natywnie
 * obsługuje stan checked, brand color z tokens.
 *
 * **NIE używamy Field-a** — Field zakłada że children to single control z
 * label-em na zewnątrz; checkbox ma label-wrap, więc layout jest inny.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox({ label, children, error, className, ...rest }, ref) {
    const text = children ?? label;
    const labelEl = (
      <label className={`${styles.choiceLabel}${className ? ` ${className}` : ""}`}>
        <input
          ref={ref}
          type="checkbox"
          className={styles.choiceInput}
          {...rest}
          aria-invalid={error ? true : rest["aria-invalid"]}
        />
        {text != null && <span className={styles.choiceText}>{text}</span>}
      </label>
    );

    if (!error) return labelEl;

    return (
      <div className={styles.checkboxField}>
        {labelEl}
        <span className={styles.errorText} role="alert">
          {error}
        </span>
      </div>
    );
  },
);

export type CheckboxProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "children"
> & {
  /** Tekst label-a (alternatywnie podaj `children`). */
  label?: ReactNode;
  /** Bogaty content label-a (np. z `<a>` linkiem do regulaminu). */
  children?: ReactNode;
  /** Komunikat błędu walidacji — renderowany pod checkboxem ze stylem
   *  identycznym do `Field` (czerwony tekst + ikona warning). Aktywuje też
   *  `aria-invalid`. */
  error?: string;
};
